import React, { useState, useImperativeHandle, useEffect, useCallback, useRef, useMemo } from 'react';
import { Form, Grid, Button, Icon } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import ReactToPrint, { PrintContextConsumer } from 'react-to-print';
import _ from 'lodash';
import { useFormik } from 'formik';
import toastr from 'toastr';
import {
  ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING,
  ATTRIBUTE_TYPE,
  mapValuesToFormDataAndUdpatedSignatures,
  filterChildPicklist,
  HTML_INPUT_ELEMENT_TYPE_OPTIONS,
  allFormElementForEachFunc,
  mapValuesToSchema,
  computeIfElementIsRequired,
  updateComputedDefaultIfValue,
  METADATA_ASSET_LIST,
} from './FormUtils';
import styles from './FormPreview.module.css';
import { arrowDownSvg, arrowUpSVG, printIconSVG } from '../../../HelperMethods/constants';
import { formikPropType } from '../../../common/prop-types';
import Asciicheck from '../../../common/Asciicheck';
import { shouldCheckForAutoResolveFirstTask } from '../../../HelperMethods/TaskUtilities';
import QRCodePopup from '../QRCodePopup';
import { urlGeneratorForQRCode } from './FormHelper';
import { updateAssetSearchPicklistForForm } from '../../taskView/TaskViewPage/ManageTaskForm/redux/actions';

const debouncedFn = {};

function doesObjectConformsRequiredValidation(obj) {
  for (const value of Object.values(obj)) {
    if (!value) return false;
  }
  return true;
}

function validateFormElement(
  element,
  formValue,
  conditionRequired = false,
  createConditionRequired = false,
  resolveConditionRequired = false
) {
  let isFormElementValid = true;
  let errorMsg;
  const { type, inputType, minVar, maxVar, errorMessage, defaultValue = '' } = element.attributeObj;
  if (
    (createConditionRequired || resolveConditionRequired || conditionRequired) &&
    (!formValue ||
      (Array.isArray(formValue) && !formValue.length) ||
      (typeof formValue === 'object' && !doesObjectConformsRequiredValidation(formValue)))
  ) {
    isFormElementValid = false;
    errorMsg = errorMessage || 'Value is required';
  } else if (
    type === ATTRIBUTE_TYPE.INPUT_BOX &&
    inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.NUMBER.id &&
    (minVar || maxVar)
  ) {
    const isDefaultVal = _.isNumber(defaultValue) && parseInt(formValue) === parseInt(defaultValue);
    if (!isDefaultVal && (parseInt(formValue, 10) < minVar || parseInt(formValue, 10) > maxVar)) {
      isFormElementValid = false;
      errorMsg = errorMessage || 'Invalid Value';
    }
  } else if (
    type === ATTRIBUTE_TYPE.INPUT_BOX &&
    inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.DECIMAL.id &&
    (minVar || maxVar)
  ) {
    const isDefaultVal = !isNaN(parseFloat(defaultValue)) && parseFloat(formValue) === parseFloat(defaultValue);
    if (!isDefaultVal && (parseFloat(formValue) < minVar || parseFloat(formValue) > maxVar)) {
      isFormElementValid = false;
      errorMsg = errorMessage || 'Invalid Value';
    }
  } else if (
    type === ATTRIBUTE_TYPE.INPUT_BOX &&
    inputType === HTML_INPUT_ELEMENT_TYPE_OPTIONS.TEXT.id &&
    (minVar || maxVar)
  ) {
    const isDefaultVal = defaultValue && formValue === defaultValue;
    if (!isDefaultVal && (_.size(formValue) < minVar || _.size(formValue) > maxVar)) {
      isFormElementValid = false;
      errorMsg = errorMessage;
    }
  }

  return { isFormElementValid, errorMsg };
}

function getValues({ task, formData, taskformTouch, formik, attributeName }) {
  let val = task?.id ? formData : { data: formik?.values, isFormikValue: true };
  const isFieldTouched = _.get(taskformTouch, attributeName);

  if (isFieldTouched) {
    val = { data: formik?.values, isFormikValue: true };
  }
  return val;
}

const getAssigneeDetails = ({ assigneeOptions, taskformValues, selectedTasktype }) => {
  let operatorOptions = _.get(assigneeOptions, 'items', []).map((val) => val.originalobject);
  if (selectedTasktype?.originalobject?.needEquipment) {
    operatorOptions = _.get(assigneeOptions, 'items', [])
      .filter((a) => !a.originalobject.isEquipmentResource)
      .map((val) => val.originalobject);
  }
  const operatorDict = _.keyBy(operatorOptions, 'id');
  const selectedAssigneeDetails = (taskformValues?.s_assigneeid || [])
    .map((id) => operatorDict[id])
    .filter((val) => val);
  return selectedAssigneeDetails.map((assignee) => _.pick(assignee, ['id', 'firstName', 'lastName', 'userName']));
};

const FormPreview = React.forwardRef((props, ref) => {
  const {
    formData,
    originalSchema,
    picklistDict,
    allPicklistData,
    taskformValues,
    picklistMappings,
    selectedTasktype,
    task = {},
    elements,
    taskformTouch,
    assigneeOptions,
    parentTask = null,
    onlyForPrint = false,
    buttonStyle,
    printLabel = '',
    showQr = false,
    ifSmallScreen = false,
  } = props;

  let printComponentRef;
  const qrModalRef = useRef();
  const dispatch = useDispatch();

  const [activeSection, openSection] = useState([]);

  const docTaskNumber = task.taskNumber;
  const docId = parentTask ? parentTask.id : task.id;

  const documentTitle = originalSchema ? `${originalSchema.name} - ${docTaskNumber || ''}` : '';

  const handleMapValueToSchema = useCallback(
    (manifestFormData) => {
      const x = mapValuesToSchema({
        ...props,
        schema: originalSchema?.data || { elements },
        values: manifestFormData,
        picklist: Object.values(picklistDict || {}),
        task,
        selectedTasktype,
        taskformValues,
        edit: !_.isNil(task?.id),
      });
      return x;
    },
    [
      selectedTasktype,
      assigneeOptions,
      _.get(taskformValues, 's_assigneeid'),
      _.get(taskformValues, 's_stop'),
      _.get(taskformValues, 's_assetId'),
      props.searchPicklistForForm,
    ]
  );

  const [formSchemaState, handleFormSchemaState] = useState({});

  const handleFormSchema = (params) => {
    handleFormSchemaState(params);
  };

  const formik = useFormik({
    initialValues: formSchemaState.formikInitValue,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (Object.keys(props?.searchPicklistForForm ?? {})?.length) {
      const mappedValues = handleMapValueToSchema({ data: formik?.values, isFormikValue: true });
      handleFormSchema(mappedValues);
    }
  }, [props.searchPicklistForForm]);

  useEffect(() => {
    const val = getValues({ task, formData, taskformTouch, formik });
    const mappedValues = handleMapValueToSchema(val);
    handleFormSchema(mappedValues);
  }, [selectedTasktype, assigneeOptions]);

  useEffect(() => {
    const val = getValues({ task, formData, taskformTouch, formik, attributeName: 's_assigneeid' });
    const mappedValues = handleMapValueToSchema(val);
    handleFormSchema(mappedValues);
  }, [_.get(taskformValues, 's_assigneeid')]);

  useEffect(() => {
    const val = getValues({ task, formData, taskformTouch, formik, attributeName: 's_stop' });
    const mappedValues = handleMapValueToSchema(val);
    handleFormSchema(mappedValues);
  }, [_.get(taskformValues, 's_stop')]);

  useEffect(() => {
    const val = getValues({ task, formData, taskformTouch, formik, attributeName: 's_assetId' });
    const mappedValues = handleMapValueToSchema(val);
    handleFormSchema(mappedValues);
  }, [_.get(taskformValues, 's_assetId')]);

  const handleToggleSection = useCallback(
    (id) => {
      const clonedActiveSection = _.clone(activeSection);
      const reqIndex = clonedActiveSection.indexOf(id);
      if (reqIndex > -1) {
        clonedActiveSection.splice(reqIndex, 1);
      } else {
        clonedActiveSection.push(id);
      }
      openSection(clonedActiveSection);
    },
    [activeSection]
  );

  const handleOpenSection = useCallback(
    (sectionIds) => {
      const newActiveOpenSections = Array.from(new Set([...activeSection, ...sectionIds]));
      openSection(newActiveOpenSections);
    },
    [activeSection]
  );

  const handleChange = (e, data, type) => {
    let val = '';

    const event = {
      target: {
        value: data?.value,
      },
    };
    if (!(type === ATTRIBUTE_TYPE.DROPDOWN || type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST)) {
      val = Asciicheck(event)?.target?.value;
    } else {
      val = data?.value;
    }
    // if (!Asciicheck(event) {
    //   val = data.value;
    // }
    const formVal = { ..._.cloneDeep(formik.values), [data.name]: val };
    formik.setFieldValue(data.name, val, false);
    formik.setFieldTouched(data.name, true, false);
    if (type === ATTRIBUTE_TYPE.DROPDOWN) {
      filterChildPicklist({
        handleFormSchema,
        formVal,
        formSchemaState,
        allPicklistData,
        taskformValues,
        picklistMappings,
        selectedTasktype,
        formik,
        picklistDict,
        data,
        ...props,
      });
    } else if (type === ATTRIBUTE_TYPE.INPUT_BOX) {
      updateComputedDefaultIfValue({ formSchemaState, formVal, formik, handleFormSchema, data });
    }
  };

  const handleMapValue = () => {
    const { data, updatedSignatures } = mapValuesToFormDataAndUdpatedSignatures({
      schema: formSchemaState.schema,
      formValues: formik.values,
      formAttribute: formSchemaState.formAttributeValue,
      formData,
      originalSchema,
      picklistDict: { ...picklistDict, ...props.searchPicklistForForm },
    });
    return { data, updatedSignatures };
  };

  const validationFunc = (params = { validateEveryField: false, isTaskResolved: false, isCreate: false }) => {
    const { validateEveryField, isTaskResolved, isCreate } = params;
    const values = _.cloneDeep(formik.values);
    let isValid = true;
    formik.setErrors({});
    const sectionsToOpen = [];
    const { autoResolveFirstTaskValidation, sectionToValidate } = shouldCheckForAutoResolveFirstTask({
      tt: selectedTasktype?.originalobject,
      task,
    });
    // omit metadatadynamic list in case of autoResolveFirstTaskValidation
    allFormElementForEachFunc(formSchemaState.schema, (element, parent) => {
      if (element.type === ATTRIBUTE_TYPE.SECTION || element.type === ATTRIBUTE_TYPE.EXPANDING_COMBO) return;
      const reqAttribute = element.attributeObj;
      const { disabled, sectionId, isRequiredOnCreation, isRequired, id: key, type: attributeType } = reqAttribute;
      // const isCommonElement = !sectionId;
      const { conditionRequired, isElementShown } = computeIfElementIsRequired(
        formSchemaState?.formAttributeValue ?? {},
        element.attributeObj,
        values
      );
      const createConditionRequired = isCreate && isRequiredOnCreation;
      const resolveConditionRequired = isTaskResolved && isRequired;
      if (isElementShown) {
        if (parent?.type === ATTRIBUTE_TYPE.EXPANDING_COMBO) {
          const expandingComboValue = _.get(values, parent.id);
          for (let i = 0; i < expandingComboValue.length; i += 1) {
            const dataValueGetPath = `${parent.id}[${i}].${element.id}`;
            const dataValue = _.get(values, dataValueGetPath);
            // const isFieldTouched = _.get(formik.touched, dataValueGetPath);
            if (!disabled && (validateEveryField || conditionRequired)) {
              const { errorMsg, isFormElementValid } = validateFormElement(element, dataValue, conditionRequired);
              if (!isFormElementValid) formik.setFieldError(dataValueGetPath, errorMsg);
              isValid = isValid && isFormElementValid;
              if (!isFormElementValid && sectionId) {
                sectionsToOpen.push(sectionId);
              }
            }
          }
          return;
        }
        const dataValue = _.get(values, key) || reqAttribute.value;
        // const isFieldTouched = _.get(formik.touched, key);
        const firstTaskValidation =
          autoResolveFirstTaskValidation &&
          sectionToValidate &&
          sectionToValidate === sectionId &&
          attributeType !== ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST;
        if (
          !disabled &&
          (createConditionRequired || resolveConditionRequired || conditionRequired || firstTaskValidation)
        ) {
          const { errorMsg = '', isFormElementValid = true } = validateFormElement(
            element,
            dataValue,
            conditionRequired,
            createConditionRequired,
            resolveConditionRequired
          );
          if (!isFormElementValid) formik.setFieldError(key, errorMsg);
          if (!firstTaskValidation) {
            isValid = isValid && isFormElementValid;
          }
          if (!isFormElementValid && sectionId) {
            sectionsToOpen.push(sectionId);
          }
        }
      }
    });
    handleOpenSection(sectionsToOpen);
    const isFirstSectionValid =
      sectionToValidate && autoResolveFirstTaskValidation ? !sectionsToOpen.includes(sectionToValidate) : true;
    return {
      isValid,
      isFirstSectionValid,
      invalidSectionName: !isFirstSectionValid ? formSchemaState?.formAttributeValue?.[sectionToValidate]?.label : '',
    };
  };

  useImperativeHandle(ref, () => ({
    validateFormData(params = { validateEveryField: false, isTaskResolved: false, isCreate: false }) {
      return validationFunc(params);
    },
    getFormValuesAndUpdatedSignatures() {
      const { data, updatedSignatures } = handleMapValue();
      return { data, updatedSignatures };
    },
  }));

  const preparePDFForQrCode = (printIframe) => {
    try {
      // validate
      const { isValid } = validationFunc({
        validateEveryField: taskformValues.s_status === 'resolved',
        isTaskResolved: taskformValues.s_status === 'resolved',
        isCreate: !!docId,
      });
      if (!isValid) {
        toastr.error('Form is not valid. QR is generated for valid forms');
      } else {
        // save the data to back end
        const { data: updatedFormData, updatedSignatures } = handleMapValue();
        urlGeneratorForQRCode({
          printIframe,
          qrModalRef,
          documentTitle,
          docId,
          task,
          updatedFormData,
          updatedSignatures,
        });
      }
    } catch (error) {
      toastr.error(error.message);
    }
  };

  // add search change
  // update picklist for forms

  const searchChangeDict = useMemo(() => {
    return {
      [METADATA_ASSET_LIST.value]: async (event, data) => {
        if (_.size(_.get(event, 'target.value', '')) > 2) {
          if (!debouncedFn[data.name]) {
            debouncedFn[data.name] = _.debounce((searchStr, selectedTT, stopId, elementData) => {
              dispatch(
                updateAssetSearchPicklistForForm({ searchString: searchStr, selectedTT, stopId, data: elementData })
              );
            }, 500);
          }
          debouncedFn[data.name](
            _.get(event, 'target.value', ''),
            selectedTasktype,
            _.get(taskformValues, 's_stop'),
            data
          );
        }
      },
    };
  }, [selectedTasktype, _.get(taskformValues, 's_stop')]);

  return (
    <>
      {docTaskNumber && showQr ? (
        <ReactToPrint
          content={() => printComponentRef}
          documentTitle={documentTitle}
          print={(printIframe) => {
            preparePDFForQrCode(printIframe);
          }}
        >
          <PrintContextConsumer>
            {({ handlePrint }) => (
              <Button
                onClick={handlePrint}
                icon
                type="button"
                style={buttonStyle || { left: '87%', top: '-24px', position: 'absolute', padding: '4px 5px' }}
              >
                <Icon name="qrcode" />
              </Button>
            )}
          </PrintContextConsumer>
        </ReactToPrint>
      ) : null}
      <ReactToPrint content={() => printComponentRef} documentTitle={documentTitle}>
        <PrintContextConsumer>
          {({ handlePrint }) => (
            <Button
              type="button"
              onClick={handlePrint}
              style={buttonStyle || { left: '83%', top: '-24px', position: 'absolute', padding: '4px 5px' }}
            >
              <img
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(printIconSVG())}`}
                height="12px"
                width="12px"
                style={{ verticalAlign: 'middle' }}
              />
              {printLabel ? <span style={{ textTransform: 'none' }}>&nbsp;&nbsp;{printLabel}</span> : ''}
            </Button>
          )}
        </PrintContextConsumer>
      </ReactToPrint>
      <div style={{ display: 'none' }}>
        <PrintFormRenderer
          ref={(el) => {
            printComponentRef = el;
          }}
          handleOpenSection={handleToggleSection}
          activeSection={activeSection}
          formik={formik}
          handleChange={handleChange}
          print
          formSchemaState={formSchemaState}
          docTaskNumber={docTaskNumber}
          onlyForPrint={onlyForPrint}
          searchChangeDict={searchChangeDict}
          {...props}
        />
      </div>

      {!onlyForPrint && (
        <FormRenderer
          ref={ref}
          handleOpenSection={handleToggleSection}
          activeSection={activeSection}
          formik={formik}
          handleChange={handleChange}
          formSchemaState={formSchemaState}
          print={false}
          docTaskNumber={docTaskNumber}
          searchChangeDict={searchChangeDict}
          ifSmallScreen={ifSmallScreen}
          {...props}
        />
      )}
      <QRCodePopup ref={qrModalRef} />
    </>
  );
});
FormPreview.propTypes = {
  // formSchema: PropTypes.object.isRequired,
  picklistDict: PropTypes.object,
  formData: PropTypes.any,
  task: PropTypes.any,
  originalSchema: PropTypes.any,
  loggedInUser: PropTypes.any,
  width: PropTypes.string,
  allPicklistData: PropTypes.array,
  taskformValues: PropTypes.object,
  picklistMappings: PropTypes.array,
  selectedTasktype: PropTypes.object,
  elements: PropTypes.object,
  taskformTouch: PropTypes.object,
  assigneeOptions: PropTypes.object.isRequired,
  parentTask: PropTypes.any,
  onlyForPrint: PropTypes.any,
  buttonStyle: PropTypes.object,
  printLabel: PropTypes.string,
  showQr: PropTypes.bool,
  searchPicklistForForm: PropTypes.object,
  ifSmallScreen: PropTypes.bool,
};

const FormRenderer = ({
  formSchemaState,
  print,
  handleOpenSection,
  activeSection,
  formik,
  handleChange,
  docTaskNumber,
  ifSmallScreen,
  ...props
}) => {
  const { assigneeOptions, taskformValues, selectedTasktype, searchChangeDict, onlyForPrint = false } = props;
  const assigneeDetails = getAssigneeDetails({ assigneeOptions, taskformValues, selectedTasktype });
  const elements = (formSchemaState?.schema || [])
    .map((item) => {
      let element = null;
      if (item.type === ATTRIBUTE_TYPE.SECTION) {
        element = (
          <SectionPreview
            item={item}
            handleOpenSection={handleOpenSection}
            activeSection={activeSection}
            formik={formik}
            handleChange={handleChange}
            print={print}
            formAttributesObj={formSchemaState?.formAttributeValue ?? {}}
            assigneeDetails={assigneeDetails}
            onlyForPrint={onlyForPrint}
            searchChangeDict={searchChangeDict}
            ifSmallScreen={ifSmallScreen}
          />
        );
      } else {
        const reqAttr = ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING[item.type]({
          attributeObj: item.attributeObj,
          formik,
          handleChange,
          item,
          print,
          formAttributesObj: formSchemaState?.formAttributeValue ?? {},
          assigneeDetails,
          onlyForPrint,
          searchChangeDict,
        });
        element = reqAttr ? <Form.Field className="no-break ">{reqAttr}</Form.Field> : null;
      }
      return { ...item, element };
    })
    .filter((item) => item.element);
  return (
    <Form style={{ width: '100%' }} id="printFormContainer" className="print-form-container" autoComplete="off">
      {print && docTaskNumber && <span className="task-number-span">Task Number: {docTaskNumber}</span>}
      <Grid className={`${styles.formContainer} print-form-container-grid`}>
        <Grid.Row className="print-form-container-main-row">
          <Grid.Column width={16}>
            <Grid style={{ marginTop: '0px', marginBottom: '0px' }}>
              <Grid.Row className="print-form-container-grid-row">
                {elements.map((item) => {
                  let size = item.size ? item.size : 1;
                  if (ifSmallScreen && !print) {
                    size = 1;
                  }
                  return (
                    <Grid.Column
                      width={16 * size}
                      style={{ paddingLeft: '0px', paddingRight: '0px' }}
                      key={item.id}
                      // className="outer-column"
                      className={`outer-column ${
                        item.type === ATTRIBUTE_TYPE.SECTION ? 'section' : 'outer-non-section-column'
                      }`}
                    >
                      <>{item.element}</>
                    </Grid.Column>
                  );
                })}
              </Grid.Row>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Form>
  );
};

const PrintFormRenderer = React.forwardRef((props, ref) => {
  return (
    <div ref={ref}>
      <FormRenderer {...props} />
    </div>
  );
});

FormRenderer.propTypes = {
  formSchemaState: PropTypes.object.isRequired,
  // width: PropTypes.string,
  print: PropTypes.bool,
  handleOpenSection: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  activeSection: PropTypes.arrayOf(PropTypes.string).isRequired,
  formik: formikPropType.isRequired,
  docTaskNumber: PropTypes.any,
  assigneeOptions: PropTypes.array,
  taskformValues: PropTypes.object,
  selectedTasktype: PropTypes.object,
  onlyForPrint: PropTypes.bool,
  searchChangeDict: PropTypes.object,
  ifSmallScreen: PropTypes.bool,
};

const SectionPreview = ({
  item,
  handleOpenSection,
  activeSection,
  formik,
  handleChange,
  print,
  formAttributesObj,
  assigneeDetails,
  onlyForPrint = false,
  searchChangeDict,
  ifSmallScreen = false,
}) => {
  const isActive = activeSection.includes(item.id) || print;

  const sectionElements = (item.elements || [])
    .map((obj) => {
      const attr = ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING[obj.type]({
        attributeObj: obj.attributeObj,
        formik,
        handleChange,
        print,
        item: obj,
        formAttributesObj,
        assigneeDetails,
        onlyForPrint,
        searchChangeDict,
      });
      const element = attr ? <Form.Field className="no-break">{attr}</Form.Field> : null;
      return { ...obj, element };
    })
    .filter((obj) => obj.element);
  return (
    <div className="section-container">
      <div className={`${styles.sectionHeaderConainer} section-header-container`}>
        <div
          className={`${styles.sectionHeader} section-header`}
          onClick={() => handleOpenSection(item.attributeObj.id)}
        >
          {item.attributeObj.label}
          <span className={`${styles.sectionImageContainer} section-image-container`}>
            <img
              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                isActive ? arrowUpSVG() : arrowDownSvg('black')
              )}`}
              height="12px"
              width="12px"
              style={{ cursor: 'pointer' }}
            />
          </span>
        </div>
      </div>
      <div style={{ display: isActive ? 'block' : 'none' }} key={item.id} className="section-body-container">
        <Grid.Column width={16}>
          <Grid style={{ margin: '0' }}>
            <Grid.Row>
              {sectionElements.map((obj) => {
                let printSize = print && obj.type === ATTRIBUTE_TYPE.SINGLE_CHOICE ? 0.5 : false;
                if (!printSize) {
                  printSize = !print && ifSmallScreen ? 1 : obj.size;
                }
                return (
                  <Grid.Column
                    // eslint-disable-next-line no-unneeded-ternary
                    width={16 * (printSize ? printSize : 1)}
                    style={{ paddingLeft: '0px', paddingRight: '0px' }}
                    key={obj.id}
                    className="section-column"
                  >
                    <Form.Field className="no-break">{obj.element}</Form.Field>
                  </Grid.Column>
                );
              })}
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </div>
    </div>
  );
};

SectionPreview.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    attributeObj: PropTypes.object,
    elements: PropTypes.array,
  }).isRequired,
  handleOpenSection: PropTypes.func.isRequired,
  activeSection: PropTypes.arrayOf(PropTypes.string).isRequired,
  formik: formikPropType.isRequired,
  handleChange: PropTypes.func.isRequired,
  print: PropTypes.bool,
  formAttributesObj: PropTypes.object.isRequired,
  assigneeDetails: PropTypes.array,
  onlyForPrint: PropTypes.bool,
  searchChangeDict: PropTypes.object,
  ifSmallScreen: PropTypes.bool,
};
SectionPreview.defaultProps = {
  print: false,
};

export default FormPreview;
